import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, Grid, Typography, TextField, MenuItem, FormControlLabel, Switch, Button, IconButton, CircularProgress, makeStyles } from "@material-ui/core";
import { ArrowUpward, ArrowDownward, DeleteOutline, AndroidOutlined } from "@material-ui/icons";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { maxWidth: 1200, width: "100%", margin: "0 auto", minWidth: 0 },
  title: { fontWeight: 700, marginBottom: 8 },
  subtitle: { color: theme.palette.text.secondary, marginBottom: 24 },
  card: { padding: 24, border: "1px solid " + theme.palette.divider, boxShadow: "none", borderRadius: 14,
    [theme.breakpoints.down("xs")]: { padding: 16 } },
  section: { marginBottom: 20 },
  row: { display: "flex", alignItems: "center", gap: 4, padding: "8px 0", borderBottom: "1px solid " + theme.palette.divider },
  name: { flex: 1, minWidth: 0, overflowWrap: "anywhere", fontWeight: 600 },
  number: { width: 24, flexShrink: 0, color: theme.palette.primary.main, fontWeight: 700 },
  preview: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", background: theme.palette.action.hover, padding: 20, borderRadius: 12, lineHeight: 1.7, margin: "16px 0" },
  action: { display: "flex", justifyContent: "flex-end", marginTop: 24 },
  loading: { padding: 32, textAlign: "center" },
}));

const empty = { enabled: false, queueIds: [], humanQueueId: null, greetingMessage: "" };
export default function Chatbot() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps = [], loading: connectionsLoading } = useContext(WhatsAppsContext);
  const [accountId, setAccountId] = useState("");
  const [config, setConfig] = useState(empty);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!whatsApps.some(w => w.id === Number(accountId))) setAccountId(whatsApps[0]?.id || "");
  }, [whatsApps, accountId]);
  useEffect(() => {
    if (!accountId || user.profile !== "admin") { setReady(false); return; }
    let active = true;
    setLoading(true); setReady(false);
    api.get("/chatbot/" + accountId).then(({ data }) => {
      if (!active) return;
      setConfig({ ...data.config, greetingMessage: data.whatsapp.greetingMessage ||
        "Olá, {{name}}! Bem-vindo ao nosso atendimento. Escolha o setor com que deseja falar." });
      setQueues(data.queues);
      setReady(true);
    }).catch(error => { if (active) toastError(error); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accountId, user.profile]);
  if (user.profile !== "admin") return <Typography>Somente administradores podem configurar o chatbot.</Typography>;
  const selected = config.queueIds.map(id => queues.find(q => q.id === id)).filter(Boolean);
  const changeOrder = (index, direction) => {
    const ids = [...config.queueIds];
    const next = index + direction;
    if (next < 0 || next >= ids.length) return;
    [ids[index], ids[next]] = [ids[next], ids[index]];
    setConfig({ ...config, queueIds: ids });
  };
  const add = id => {
    const queueIds = [...config.queueIds, Number(id)];
    setConfig({ ...config, queueIds, humanQueueId: config.humanQueueId || Number(id) });
  };
  const remove = id => {
    const queueIds = config.queueIds.filter(value => value !== id);
    setConfig({ ...config, queueIds,
      humanQueueId: queueIds.includes(config.humanQueueId) ? config.humanQueueId : queueIds[0] || null });
  };
  const preview = [config.greetingMessage.trim() || "Olá! Como podemos ajudar?", "",
    ...selected.map((q, index) => `${index + 1} - ${q.name}`),
    "0 - Falar com um atendente", "", "Responda com o número da opção desejada."].join("\n");
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/chatbot/" + accountId, config);
      setConfig({ ...data.config, greetingMessage: data.whatsapp.greetingMessage });
      toast.success(config.enabled ? "Chatbot ativado para esta conta." : "Chatbot desativado para esta conta.");
    } catch (error) { toastError(error); }
    finally { setSaving(false); }
  };
  return <div className={classes.root}>
    <Typography component="h1" variant="h5" className={classes.title}><AndroidOutlined style={{ verticalAlign: "middle", marginRight: 8 }} />Chatbot de atendimento</Typography>
    <Typography className={classes.subtitle}>Receba o cliente e encaminhe a conversa para o setor certo. Se houver um fluxo visual publicado, ele tem prioridade sobre este menu.</Typography>
    <Button component={RouterLink} to="/chatbot" color="primary" style={{ marginBottom: 16 }}>Voltar ao editor de fluxos</Button>
    {connectionsLoading ? <div className={classes.loading}><CircularProgress /></div>
      : !whatsApps.length ? <Paper className={classes.card}><Typography>Cadastre uma conta de WhatsApp para configurar o bot.</Typography><Button component={RouterLink} to="/connections" color="primary">Ver conexões</Button></Paper>
      : <>
        <TextField select fullWidth variant="outlined" label="Conta de WhatsApp" value={accountId}
          disabled={saving} onChange={event => setAccountId(event.target.value)} className={classes.section}>
          {whatsApps.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
        </TextField>
        {loading ? <div className={classes.loading}><CircularProgress /></div> : !ready ?
          <Button onClick={() => window.location.reload()}>Tentar novamente</Button> :
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Paper className={classes.card}>
                <FormControlLabel className={classes.section}
                  control={<Switch color="primary" checked={config.enabled} disabled={saving}
                    onChange={event => setConfig({ ...config, enabled: event.target.checked })} />}
                  label="Ativar chatbot nesta conta" />
                <TextField fullWidth multiline rows={4} variant="outlined" label="Mensagem de boas-vindas"
                  value={config.greetingMessage} disabled={saving} inputProps={{ maxLength: 2000 }}
                  helperText={"Use {{name}} para incluir o nome do cliente."}
                  onChange={event => setConfig({ ...config, greetingMessage: event.target.value })} className={classes.section} />
                <Typography component="h2" variant="h6">Setores do menu</Typography>
                <Typography variant="body2" color="textSecondary" className={classes.section}>Adicione até 10 filas e escolha a ordem das opções.</Typography>
                <TextField select fullWidth value="" label="Adicionar setor" variant="outlined"
                  disabled={saving || config.queueIds.length >= 10} onChange={event => add(event.target.value)}>
                  {queues.filter(q => !config.queueIds.includes(q.id)).map(q => <MenuItem value={q.id} key={q.id}>{q.name}</MenuItem>)}
                </TextField>
                {!queues.length && <Button component={RouterLink} to="/queues" color="primary">Cadastrar filas de atendimento</Button>}
                {selected.map((q, index) => <div className={classes.row} key={q.id}>
                  <span className={classes.number}>{index + 1}</span><span className={classes.name}>{q.name}</span>
                  <IconButton size="small" aria-label={"Mover " + q.name + " para cima"} disabled={saving || index === 0} onClick={() => changeOrder(index, -1)}><ArrowUpward /></IconButton>
                  <IconButton size="small" aria-label={"Mover " + q.name + " para baixo"} disabled={saving || index === selected.length - 1} onClick={() => changeOrder(index, 1)}><ArrowDownward /></IconButton>
                  <IconButton size="small" aria-label={"Remover " + q.name} disabled={saving} onClick={() => remove(q.id)}><DeleteOutline /></IconButton>
                </div>)}
                <TextField select fullWidth variant="outlined" label="Fila para falar com atendente (opção 0)"
                  value={config.humanQueueId || ""} disabled={saving || !selected.length}
                  onChange={event => setConfig({ ...config, humanQueueId: Number(event.target.value) })}
                  style={{ marginTop: 24 }} helperText="Após escolher um setor, o bot para e o ticket aguarda atendimento humano.">
                  {selected.map(q => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                </TextField>
                <div className={classes.action}><Button variant="contained" color="primary" onClick={save}
                  disabled={saving || (config.enabled && (!selected.length || !config.humanQueueId || !config.greetingMessage.trim()))}>
                  {saving ? "Salvando…" : "Salvar configuração"}
                </Button></div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper className={classes.card}><Typography component="h2" variant="h6">Prévia da mensagem</Typography>
                <div className={classes.preview}>{preview}</div>
                <Typography variant="body2" color="textSecondary">O menu responde somente em conversas individuais sem setor ou atendente definidos. A conta precisa estar conectada.</Typography>
              </Paper>
            </Grid>
          </Grid>}
      </>}
  </div>;
}
